/**
 * NP-505 — which loan officers belong to which DBA accent scheme.
 *
 * This file is the ROUTING half of DBA theming. The colours are the other half
 * and live in `src/index.css` as `[data-theme="<id>"]` blocks; `id` here is that
 * selector's value. Adding a DBA is one block there plus one entry here.
 *
 * Matching is by loan-officer email because that is the only branch signal the
 * SPA can obtain: `?lo=` carries one, and a loan's contacts expose its officer's.
 * Neither the loan nor any other response carries a branch or DBA identifier
 * today, so a branch-code lookup — which is what the API itself keys branding on
 * (ILoanOfficerBrandingResolver.ResolveBranchCodeAsync) — needs an API change
 * before it can be used here.
 *
 * <p><b>QA 2026-08-14.</b> Both lists shipped EMPTY, so nothing could ever match
 * and no DBA loan was ever themed. Every test injected its own theme fixture, so
 * the suite stayed green while the shipped routing was dead — see the tests for
 * the guard that now covers the real table.</p>
 */

export interface DbaTheme {
  /** Matches the `[data-theme="…"]` selector in index.css. */
  id: string;
  /** DBA trading name, for logs and for whoever maintains this list. */
  name: string;
  /**
   * Exact loan-officer addresses, lower-case. AFN loan officers use
   * `@afncorp.com` regardless of the DBA they trade under, so a domain match
   * would catch every branch — those addresses have to be listed individually.
   */
  loanOfficerEmails: readonly string[];
  /**
   * Email domains wholly owned by this DBA, lower-case and without the `@`.
   *
   * A DBA that puts its officers on its own domain does not need every address
   * listed — which matters, because listing them one at a time is how this table
   * came to be empty. NEVER put `afncorp.com` here: AFN officers trade under
   * every DBA, so it would colour the whole portal for one branch.
   */
  loanOfficerDomains?: readonly string[];
}

export const DBA_THEMES: readonly DbaTheme[] = [
  {
    id: 'directmyloan',
    name: 'Direct My Loan',
    loanOfficerEmails: [],
    // DML's officers are on the DBA's own domain — confirmed from a QA loan whose
    // officer was `@directmyloan.com` — so the domain is the reliable signal and a
    // new DML officer themes correctly without touching this file. No individual
    // address is listed: the domain already covers them, and naming one person
    // here would only add something to maintain when they move on.
    loanOfficerDomains: ['directmyloan.com'],
  },
  {
    id: 'superloans',
    name: 'Superloans',
    loanOfficerEmails: [],
    // Supplied on NP-563: QA's Superloans entry link carries
    // `?lo=rhartz@superloans.com`. Routed by domain for the same reason as DML
    // above — a new Superloans officer themes without another edit here — and
    // because listing one person is what left this DBA unrouted since NP-505.
    loanOfficerDomains: ['superloans.com'],
  },
];
